<template>
  <div class="section energy-demand">
    <div class="section__header">Zapotrzebowanie na energię</div>
    <div class="energy-demand-type">
      <div class="label">Sposób przeliczania KBTW:</div>
      <BaseRadio
        class="energy-demand-type__radio"
        :model-value="standard"
        label="Standardowy"
        @update:model-value="$event && emit('mode', 'standard')"
      />
      <BaseRadio
        class="energy-demand-type__radio"
        :model-value="sport"
        label="Dla sportowców"
        @update:model-value="$event && emit('mode', 'sport')"
      />
    </div>
    <div v-if="standard" class="energy-demand__standard">
      <div class="energy-demand__calories">
        <div class="energy-demand__header">Wartość energetyczna</div>
        <div class="energy-demand__info">
          <div class="energy-demand__info-left">(kcal)</div>
        </div>
        <div class="energy-demand__calories-box">
          <BaseInputNumber
            test-id="DietDetailAssumptions__Calories"
            class="energy-demand__input"
            :min="1"
            :max="9999"
            :model-value="calories"
            @update:model-value="emit('norm', 'calories', $event)"
          />
        </div>
      </div>
      <MacronutrientControl
        v-for="macro in standardMacros"
        :key="macro.nutrient"
        v-bind="macro"
        mode="standard"
        @update="emit('calculation', macro.nutrient, $event, 'standard')"
      />
    </div>
    <div v-if="sport" class="energy-demand__sport">
      <div class="energy-demand__weight">
        <div class="energy-demand__header">Masa ciała pacjenta</div>
        <div class="energy-demand__info">
          <div class="energy-demand__info-left">(kg)</div>
        </div>
        <div class="energy-demand__weight-box">
          <BaseInputNumber
            test-id="DietDetailAssumptions__Sport__Weight"
            class="energy-demand__input"
            :precision="1"
            :step="0.1"
            :min="0.1"
            :max="250"
            :model-value="weight"
            @update:model-value="emit('weight', $event)"
          />
        </div>
      </div>
      <MacronutrientControl
        v-for="macro in sportMacros"
        :key="macro.nutrient"
        v-bind="macro"
        mode="sport"
        @update="emit('calculation', macro.nutrient, $event, 'sport')"
      />
    </div>
    <div v-if="standard && normsActive" class="info">
      <Tooltip placement="top">
        <template #trigger>
          <div class="energy-demand__info-tooltip">
            <BaseIcon name="info" />
            Przybliżona wartość energetyczna na podstawie poniższych norm to
            <span class="info--bold">{{ caloriesFromNorms }} kcal</span>
          </div>
        </template>
        <span class="energy-demand__info-tooltip-content">
          Przybliżona wartość energetyczna została obliczona według wzoru na całkowitą przemianę
          materii oraz danych wpisanych w sekcji "Normy"
        </span>
      </Tooltip>
    </div>
    <div v-if="sport" class="info">
      <BaseIcon name="info" />
      Przybliżona wartość energetyczna to
      {{ calories }}
      kcal
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { computed } from 'vue'

  import BaseIcon from '@/components/BaseIcon.vue'
  import BaseInputNumber from '@/components/BaseInputNumber.vue'
  import BaseRadio from '@/components/BaseRadio.vue'
  import { Tooltip } from '@/components/ui'

  import type { DietCalculationType } from '@/types/Diet'
  import type { Nutrients } from '@/utils/nutrients'

  import MacronutrientControl from '../MacronutrientControl/MacronutrientControl.vue'

  type Macro = 'protein' | 'fat' | 'carbohydrates'
  const props = defineProps<{
    calories: number
    caloriesFromNorms: number
    carbohydratesGrams: number
    fatGrams: number
    normsActive: boolean
    proteinGrams: number
    sport: boolean
    sportCarbohydrates: number
    sportFat: number
    sportProtein: number
    standard: boolean
    standardCarbohydrates: number
    standardFat: number
    standardProtein: number
    weight: number
  }>()
  const emit = defineEmits<{
    calculation: [key: Macro, value: number, type: DietCalculationType]
    mode: [type: DietCalculationType]
    norm: [key: Nutrients, value: number]
    weight: [value: number]
  }>()
  const baseMacros = computed(() => [
    {
      color: 'pink',
      grams: props.proteinGrams,
      label: 'Białko ogółem',
      nutrient: 'protein' as const,
      percentage: props.standardProtein,
    },
    {
      color: 'yellow',
      grams: props.fatGrams,
      label: 'Tłuszcze',
      nutrient: 'fat' as const,
      percentage: props.standardFat,
    },
    {
      color: 'purple',
      grams: props.carbohydratesGrams,
      label: 'Węglowodany przyswajalne',
      nutrient: 'carbohydrates' as const,
      percentage: props.standardCarbohydrates,
    },
  ])
  const standardMacros = computed(() =>
    baseMacros.value.map(macro => ({
      ...macro,
      inputValue: macro.percentage,
      tooltip:
        macro.nutrient === 'carbohydrates'
          ? '+ wartość energetyczna pochodząca z błonnika'
          : undefined,
    })),
  )
  const sportValues = computed<Record<Macro, number>>(() => ({
    carbohydrates: props.sportCarbohydrates,
    fat: props.sportFat,
    protein: props.sportProtein,
  }))
  const sportMacros = computed(() =>
    baseMacros.value.map(macro => ({
      ...macro,
      inputValue: sportValues.value[macro.nutrient],
    })),
  )
</script>
